import React, { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';

import { useApolloClient } from '@apollo/client';

import {
  CatalogProductDetailViewFragmentFragment,
  CatalogVariantFragmentFragment,
} from '@notino/shared/definitions/types';
import getSeoMetadataQuery from '@notino/shared/graphql/getSeoMetaDataQuery.graphql';

import { useImageAttributes } from '@components/CdnImage/useImageAttributes';
import { useSsrProductUrl } from '@containers/ProductDetailContainer/ProductDetail/hooks/useSsrProductUrl';

import { getProductName } from '../utils';

import { headMetaMessages } from './messages';

interface HeadMetaReviewsPageProps {
  product: CatalogProductDetailViewFragmentFragment;
  variant: CatalogVariantFragmentFragment;
  simpleDescription: string | null;
}

const MAX_DESCRIPTION_LENGTH = 200;

export const HeadMetaReviewsPage: FC<HeadMetaReviewsPageProps> = ({
  product,
  variant,
  simpleDescription,
}) => {
  const { url } = useSsrProductUrl(product);
  const { formatMessage } = useIntl();
  const { src: imageSrc } = useImageAttributes(
    variant.media?.mainImage?.src ?? '',
    'metaTag'
  );
  const { cache } = useApolloClient();

  const { hostname, origin } = url
    ? new URL(url)
    : { hostname: undefined, origin: undefined };

  const productName = getProductName(variant, product);

  const title = formatMessage(headMetaMessages.seoTitle, { productName });

  const keywords = formatMessage(headMetaMessages.seoKeywords, {
    productName,
    productShortName: `${product.brand?.name ?? ''} ${
      product.collection ?? ''
    }`,
  });

  const productReviewsUrl = formatMessage(headMetaMessages.seoUrl, {
    productUrl: product.url,
  });

  const description = simpleDescription
    ? simpleDescription.slice(0, MAX_DESCRIPTION_LENGTH)
    : variant.metaData.description;

  cache.writeQuery({
    query: getSeoMetadataQuery,
    data: {
      description,
      keywords,
      title,
    },
  });

  return (
    <Helmet>
      <meta property="url" content={productReviewsUrl} />
      <meta property="og:type" content="reviews" />
      <meta property="og:url" content={productReviewsUrl} />
      <meta property="og:description" content={description ?? undefined} />
      <meta property="og:title" content={title} />
      <meta property="og:image" content={imageSrc} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={hostname} />
      <meta name="robots" content="index, follow" />

      <link rel="canonical" href={`${origin ?? ''}${productReviewsUrl}`} />
    </Helmet>
  );
};
